import i18n from '@/i18n'
import { useCallback, useRef, useState } from 'react'
import { toast } from 'sonner'
import { useAuth } from '@/contexts/AuthContext'
import { useAccountMessageSettings } from '@/hooks/useAccountMessageSettings'
import { useContactManagement } from '@/hooks/useContactManagement'
import { ensureFluxoImageDataUrl, resolveFluxoAudioUrl } from '@/lib/fluxoMedia'
import { runFluxo } from '@/lib/fluxoRunner'
import { applySenderPrefix } from '@/lib/messageSenderPrefix'
import { renderMessageVariables } from '@/lib/messageVariables'
import type { Fluxo } from '@/hooks/useFluxos'
import type { Database } from '@/lib/supabase'

type AccountContact = Database['public']['Tables']['account_contacts']['Row']

interface UseFluxoSenderParams {
  selectedContact: AccountContact | null
  sendMessage: (contactJid: string, content: string) => Promise<boolean>
  sendImage: (contactJid: string, imageBase64: string, caption?: string, fileName?: string) => Promise<boolean>
  sendVideo: (contactJid: string, videoSource: string, caption?: string, fileName?: string) => Promise<boolean>
  sendDocument: (contactJid: string, documentSource: string, fileName: string, caption?: string, mimeType?: string) => Promise<boolean>
  sendAudio: (contactJid: string, audioUrl: string) => Promise<boolean>
  onContactUpdate?: () => void
  /** Sobe a conversa na lista após um bloco de mensagem enviado com sucesso. */
  onConversationActivity?: (contactId: string) => void
}

interface FluxoSenderProgress {
  current: number
  total: number
}

interface UseFluxoSenderReturn {
  sendFluxo: (fluxo: Fluxo) => Promise<void>
  sendingFluxo: boolean
  progress: FluxoSenderProgress | null
}

export const useFluxoSender = ({
  selectedContact,
  sendMessage,
  sendImage,
  sendVideo,
  sendDocument,
  sendAudio,
  onContactUpdate,
  onConversationActivity,
}: UseFluxoSenderParams): UseFluxoSenderReturn => {
  const { user } = useAuth()
  const { showUserNameInMessages } = useAccountMessageSettings()
  const { assignAssistant, enableAssistant, addTag } = useContactManagement()
  const [sendingFluxo, setSendingFluxo] = useState(false)
  const [progress, setProgress] = useState<FluxoSenderProgress | null>(null)
  const runningRef = useRef(false)

  const sendFluxo = useCallback(async (fluxo: Fluxo): Promise<void> => {
    if (!selectedContact) {
      toast.error(i18n.t('automations:errors.pickContact'))
      return
    }

    if (runningRef.current) {
      toast.warning(i18n.t('automations:errors.waitCurrentFlow'))
      return
    }

    // O contato é capturado no início: se o usuário trocar de conversa no meio
    // do envio, o restante do fluxo continua indo para quem foi escolhido.
    const contact = selectedContact

    const bumpConversation = () => {
      onConversationActivity?.(contact.id)
    }

    runningRef.current = true
    setSendingFluxo(true)
    setProgress(null)

    const toastId = toast.loading(`Enviando fluxo "${fluxo.name}"...`)

    try {
      const result = await runFluxo(fluxo, {
        sendText: async (content) => {
          const rendered = renderMessageVariables(content, contact)
          const text = applySenderPrefix(rendered, user?.name, showUserNameInMessages)
          const ok = await sendMessage(contact.jid, text)
          if (ok) bumpConversation()
          return ok
        },
        sendImage: async (source, fileName) => {
          const dataUrl = await ensureFluxoImageDataUrl(source)
          const ok = await sendImage(contact.jid, dataUrl, '', fileName)
          if (ok) bumpConversation()
          return ok
        },
        // Vídeo e arquivo vão pela URL pública do fluxo-media: send-video/send-document
        // aceitam media_url e não precisam do round-trip em base64.
        sendVideo: async (source, fileName) => {
          const ok = await sendVideo(contact.jid, source, '', fileName)
          if (ok) bumpConversation()
          return ok
        },
        sendDocument: async (source, fileName, mimeType) => {
          const ok = await sendDocument(contact.jid, source, fileName || 'arquivo', '', mimeType)
          if (ok) bumpConversation()
          return ok
        },
        sendAudio: async (source, fileName) => {
          const audioUrl = await resolveFluxoAudioUrl(source, fileName)
          const ok = await sendAudio(contact.jid, audioUrl)
          if (ok) bumpConversation()
          return ok
        },
        activateAssistant: async (assistantId) => {
          const assigned = await assignAssistant(contact.id, assistantId)
          if (!assigned) return false

          // assignAssistant só ativa quando o contato ainda não tinha assistente;
          // no fluxo a intenção é sempre deixar o assistente escolhido respondendo.
          const enabled = await enableAssistant(contact.id)
          if (enabled) onContactUpdate?.()
          return enabled
        },
        applyTags: async (tags) => {
          // Sequencial: addTag lê e reescreve o array de tags do contato, então
          // chamadas em paralelo sobrescreveriam umas às outras.
          for (const tag of tags) {
            const name = tag.trim()
            if (!name) continue
            const ok = await addTag(contact.id, name)
            if (!ok) return false
          }
          onContactUpdate?.()
          return true
        },
        onProgress: ({ current, total }) => {
          setProgress({ current, total })
          toast.loading(`Enviando fluxo "${fluxo.name}" (${current}/${total})`, { id: toastId })
        },
      })

      if (result.status === 'empty') {
        toast.error(`O fluxo "${fluxo.name}" não tem blocos preenchidos`, { id: toastId })
        return
      }

      if (result.status === 'failed') {
        toast.error(
          `Fluxo interrompido em ${result.sent + 1} de ${result.total}: ${result.error}`,
          { id: toastId }
        )
        return
      }

      toast.success(
        `Fluxo "${fluxo.name}" enviado (${result.sent} ${result.sent === 1 ? 'bloco' : 'blocos'})`,
        { id: toastId }
      )
    } catch (err) {
      console.error('💥 [useFluxoSender] Erro ao enviar fluxo:', err)
      toast.error(err instanceof Error ? err.message : 'Erro ao enviar fluxo', { id: toastId })
    } finally {
      runningRef.current = false
      setSendingFluxo(false)
      setProgress(null)
    }
  }, [
    selectedContact,
    sendMessage,
    sendImage,
    sendVideo,
    sendDocument,
    sendAudio,
    assignAssistant,
    enableAssistant,
    addTag,
    onContactUpdate,
    onConversationActivity,
    user?.name,
    showUserNameInMessages,
  ])

  return { sendFluxo, sendingFluxo, progress }
}
